import { reactive } from 'vue'
import { defineStore } from 'pinia'
import apiClient from '@/api/client'

/**
 * Fresh signed avatar URLs, refreshed lazily when embedded ones expire.
 *
 * API responses embed S3 presigned URLs with a short TTL; a page kept
 * open past it gets 403s when the browser re-requests an image. Avatar
 * components register themselves here on mount; the FIRST image error
 * refreshes the whole registered set in ONE batched call (URLs minted
 * with the same response expire together, so waiting to collect every
 * error would gain nothing). Stragglers simply open a second batch.
 *
 * Guards: single-flight per id, plus a per-id cooldown so an error on a
 * freshly signed URL (file genuinely missing) never loops — the avatar
 * degrades to initials instead.
 */

const DEBOUNCE_MS = 100
const COOLDOWN_MS = 60_000
// Backend cap of POST /media/avatars/urls (AvatarUrlsRequest.max_length):
// the leadership tree mounts every person (>200), so larger sets are split.
const MAX_BATCH = 200

export const useAvatarsStore = defineStore('avatars', () => {
  const urls = reactive<Record<number, string>>({})

  const registered = new Map<number, number>() // id → mounted-component count
  const refreshedAt = new Map<number, number>()
  const inflight = new Set<number>()
  let pending = new Set<number>()
  let timer: ReturnType<typeof setTimeout> | null = null

  function register(id: number) {
    registered.set(id, (registered.get(id) ?? 0) + 1)
  }

  function unregister(id: number) {
    const count = (registered.get(id) ?? 1) - 1
    if (count <= 0) registered.delete(id)
    else registered.set(id, count)
  }

  function reportError(reporterId: number) {
    const now = Date.now()
    // The reporter itself may be an unregistered raw <img> (sidebar,
    // account page) — always include it alongside the registered set.
    for (const id of [reporterId, ...registered.keys()]) {
      if (inflight.has(id)) continue
      if (now - (refreshedAt.get(id) ?? 0) < COOLDOWN_MS) continue
      pending.add(id)
    }
    if (pending.size === 0) return
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => void flush(), DEBOUNCE_MS)
  }

  async function flush() {
    timer = null
    const ids = [...pending]
    pending = new Set()
    if (ids.length === 0) return
    const chunks: number[][] = []
    for (let i = 0; i < ids.length; i += MAX_BATCH) chunks.push(ids.slice(i, i + MAX_BATCH))
    await Promise.all(chunks.map(refreshBatch))
  }

  async function refreshBatch(ids: number[]) {
    ids.forEach((id) => inflight.add(id))
    try {
      const { data } = await apiClient.post<Record<string, string>>('/media/avatars/urls', {
        auth_user_ids: ids,
      })
      const now = Date.now()
      for (const id of ids) {
        refreshedAt.set(id, now)
        const fresh = data[String(id)]
        if (fresh) urls[id] = fresh
      }
    } catch {
      // Refresh failed (offline, 5xx): leave URLs as they are — avatars
      // degrade to initials. A later error retries after the cooldown.
      const now = Date.now()
      ids.forEach((id) => refreshedAt.set(id, now))
    } finally {
      ids.forEach((id) => inflight.delete(id))
    }
  }

  function urlFor(id: number | undefined): string | undefined {
    return id != null ? urls[id] : undefined
  }

  return { urls, register, unregister, reportError, urlFor }
})
